import { SettingsTabs, settingsScope, type SettingsScope } from "../connections/SettingsTabs";
import { Button, Field } from "../components/rumonai";
import { AccountModelUsage } from "./ModelSourceSettings";
import { AccountEmail } from "./AccountEmail";
import { AccountPassword } from "./AccountPassword";
import { useCallback, useEffect, useRef, useState } from "react";
import { ApiError, type LocalClientInfo } from "../lib/api";
import { CommandFeedback, PlatformError, usePlatformCommand, usePlatformRead } from "../platform/shared";
import { Icon } from "../components/Icon";
import { useDraft } from "../lib/drafts";
import { ErrorState, LoadingState } from "../shell/ui";
import { PageHead } from "../shell/PageHead";
import { Avatar } from "../shell/Avatar";
import { MenuItem, PopMenu } from "../shell/StudioChrome";
import { agentDeletionsPath, agentHref, agentSettingsRoute, hub, hubSettingsHref, instanceStatus, wakesOnDemand, type AgentInstance, type CreatedAgent } from "./api";
import { AgentRun, type AgentRunAction } from "./AgentRun";
import { useAutoRefresh } from "./autoRefresh";
import { connectionReturnHref } from "../connections/navigation";
import { AppearanceSettings } from "../shell/AppearanceSettings";
import { navigateWorkbench } from "../lib/studioNavigation";
import { useAgentFrame, type AgentFrameMessage } from "./agentFrame";
import { LocalDevice } from "./LocalDevice";
import { AgentDeletion } from "./AgentDeletion";
import "../connections/settings-list.css";
import "./OwnerSettings.css";

/**
 * 设置: one Agent's settings, or 账号.
 *
 * The one place an Agent is configured. What only the Hub can change about an
 * Agent -- its name, whether it runs, where its services come from -- is drawn
 * here; everything the Agent keeps itself is its own settings page, in a frame
 * below. Every link to an Agent's settings lands here (`HubApp`), opened at
 * what the link asked for.
 */
export function OwnerSettings({ hash, agents, accountName, onLogout, onAgentUpdated, onAgentDeleted }: {
  /** The Hub's address: which Agent, which section, and what to open its settings at. */
  hash: string;
  agents: AgentInstance[];
  accountName: string;
  onLogout?: () => void;
  onAgentUpdated?: (agent: AgentInstance) => void;
  onAgentDeleted?: (id: string) => void;
  pairingReceipts?: Record<string, CreatedAgent>;
  onPairingIssued?: (receipt: CreatedAgent) => void;
}) {
  const asked = new URLSearchParams(hash.split("?")[1]);
  const requested = asked.get("agent") || "";
  const scope = !agents.length && !requested && !asked.has("scope") ? "platform" : settingsScope(hash);
  const route = agentSettingsRoute(hash);
  const [saved, save] = useDraft("hub:settings-agent", "", "/");
  const selected = requested ? agents.find(agent => agent.id === requested) : agents.find(agent => agent.id === saved) ?? agents[0];
  const returnTo = selected ? connectionReturnHref(selected.id === requested ? hash : "", undefined, `/agents/${selected.id}`) || "#/today" : "#/today";
  const section = scope === "platform" ? "workspace" : "agent";
  const [opened, setOpened] = useState<string[]>([]);
  const [changingRuns, setChangingRuns] = useState<Record<string, AgentRunAction>>({});
  const [runErrors, setRunErrors] = useState<Record<string, unknown>>({});
  const runPending = selected ? changingRuns[selected.id] ?? null : null;
  const available = Boolean(selected && (selected.online || wakesOnDemand(selected)));
  useEffect(() => {
    if (!selected) return;
    save(selected.id);
    if (available && section === "agent") setOpened(old => old.includes(selected.id) ? old : [...old, selected.id]);
  }, [selected?.id, available, section, save]);
  const pick = (id: string) => {
    save(id);
    navigateWorkbench(hubSettingsHref(id, scope === "agent" ? "" : "#/connections?scope=" + scope));
  };
  return <div className="owner-settings">
    <PageHead title="设置" actions={<>
      {section === "agent" && selected ? <a className="btn btn-quiet settings-return" aria-label="返回原工作与对话"
        href={agentHref(selected.id, returnTo)}><Icon name="back" size={16} /><span className="settings-return-long">返回原工作与对话</span>
        <span className="settings-return-short" aria-hidden="true">返回</span></a> : null}
    </>} />
    <SettingsTabs value={scope} onChange={(value: SettingsScope) => {
      const next = new URLSearchParams(hash.split("?")[1]);
      next.set("scope", value); next.delete("namespace"); next.delete("scene"); next.delete("section");
      navigateWorkbench("#/settings?" + next);
    }} />
    {section === "agent" && agents.length && !selected ? <div className="settings-agent-head">
      <AgentPicker agents={agents} selected={selected} pick={pick} />
    </div> : null}
    {section === "workspace" ? <PlatformSettings accountName={accountName} onLogout={onLogout} /> : !selected ? <section className="connection-unavailable"><h2>{agents.length ? "请选择要配置的 Agent" : "先创建自己的 Agent"}</h2>
      <p>{agents.length ? "当前链接没有对应的 Agent。请在上方选择你的 IP 后继续。" : "每个 IP 各自保存模型、记忆与能力配置。"}</p>
      {!agents.length ? <a  href="/#/create">创建第一个 IP Agent</a> : null}</section> : <>
      <header className={"settings-agent-head" + (selected.location === "local" ? " settings-local-agent-head" : "")}>
        <AgentPicker agents={agents} selected={selected} pick={pick} />
        {scope === "agent" ? <><AgentNameEditor key={selected.id} agent={selected} onUpdated={onAgentUpdated} />
        <AgentRun key={`run:${selected.id}`} agent={selected} onUpdated={onAgentUpdated} busy={runPending}
          error={runErrors[selected.id]} onError={error => setRunErrors(current => ({ ...current, [selected.id]: error }))}
          onBusyChange={action => setChangingRuns(current => {
            if (action) return { ...current, [selected.id]: action };
            const next = { ...current };
            delete next[selected.id];
            return next;
          })} /></> : null}
      </header>
      {scope === "agent" && selected.location === "local" ? <section className="settings-group local-agent-connection" aria-label="运行位置">
        <LocalDevice agent={selected} onUpdated={onAgentUpdated} />
      </section> : null}
      {!available && selected.location !== "local" ? <p className="settings-hint">连接后可查看和修改这个 IP 的完整设置。</p> : null}
    </>}
    <PendingAgentDeletions activeIds={agents.map(agent => agent.id).join(",")} onDeleted={onAgentDeleted} />
    <div className="settings-agent-frames" hidden={section !== "agent" || !available || runPending !== null}>
      {opened.map(id => {
        const agent = agents.find(item => item.id === id);
        return agent ? <AgentSettingsFrame key={id} agent={agent} active={section === "agent" && selected?.id === id && available && !changingRuns[id]}
          route={id === requested ? route : undefined} onDeleted={onAgentDeleted} onAgentUpdated={onAgentUpdated} /> : null;
      })}
    </div>
    {scope === "agent" && selected ? <details className="settings-agent-management">
      <summary>管理 Agent</summary><AgentDeletion key={selected.id} agent={selected} onDeleted={onAgentDeleted} />
    </details> : null}
  </div>;
}

interface PendingDeletion {
  id: string;
  name: string;
  location: "cloud" | "local";
  requested_at: number;
  client_id?: string | null;
  client?: LocalClientInfo | null;
  revision?: number;
  error?: string;
}

function PendingAgentDeletions({ activeIds, onDeleted }: { activeIds: string; onDeleted?: (id: string) => void }) {
  const reading = usePlatformRead<{ deletions: PendingDeletion[] }>(agentDeletionsPath);
  const command = usePlatformCommand();
  const previousIds = useRef(activeIds);
  const { reload } = reading;
  // Follow the existing directory refresh when a revoked Agent leaves it.
  useEffect(() => {
    if (previousIds.current === activeIds) return;
    previousIds.current = activeIds;
    reload();
  }, [activeIds, reload]);
  const received = reading.data?.deletions;
  const rows = Array.isArray(received) && received.every(row => row && typeof row.id === "string"
    && typeof row.name === "string" && ["cloud", "local"].includes(row.location)
    && Number.isFinite(row.requested_at)) ? received : undefined;
  // The Hub has no stream for this; a clean-up that finished elsewhere shows when the window is back in front.
  useAutoRefresh(reload, { active: Boolean(rows?.length) });
  const error = reading.error || (reading.data !== undefined && !rows
    ? new ApiError("待清理记录未读完整", "请重新读取后再重试清理。", null, 502) : null);
  if (!error && !rows?.length && !command.notice && !command.error) return null;
  const retry = (row: PendingDeletion) => {
    void command.run(async () => {
      try {
        const result = await hub.deleteAgent(row.id, row.name);
        command.setNotice(result.cleanup_pending
          ? row.client?.online === false ? "等待 " + row.client.name + " 上线完成清理。" : "等待客户端完成清理。"
          : "清理已完成。");
        onDeleted?.(row.id);
      } finally {
        // A lost response can also mean cleanup finished; reread its authority.
        reload();
      }
    });
  };
  return <section className="settings-group pending-deletions" aria-label="待完成的删除">
    <h2 className="settings-group-title">待完成的删除</h2>
    <div className="settings-list">
      <p className="settings-notice">这些 Agent 的访问权限已撤销，文件清理还未确认完成。重试只会继续已经确认的删除。</p>
      {rows?.map(row => <div className="settings-row settings-agent-delete" key={row.id}>
        <div className="settings-row-main">
          <strong className="settings-row-title" data-quoted="true">{row.name || "未命名 Agent"}</strong>
          <p className="settings-row-desc">{row.client_id ? row.client?.online === false
            ? "等待 " + row.client.name + " 上线完成清理" : "等待客户端完成清理" : "文件清理尚未确认"}</p>
          {row.error ? <p className="settings-row-desc" role="alert" data-quoted="true">{row.error}</p> : null}
        </div>
        <div className="settings-row-side">
          <Button variant="secondary" type="button" disabled={command.pending || reading.loading}
            onClick={() => retry(row)}>{command.pending ? "正在清理…" : "重试清理"}</Button>
        </div>
      </div>)}
      {error ? <PlatformError error={error} retry={reload} /> : null}
      <CommandFeedback error={command.error} notice={command.notice} />
    </div>
  </section>;
}

function AgentNameEditor({ agent, onUpdated }: { agent: AgentInstance; onUpdated?: (agent: AgentInstance) => void }) {
  const [draft, setDraft, draftError] = useDraft<string | null>(`hub:agent-label:${agent.id}`, null, "/");
  const [editing, setEditing] = useState(draft !== null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [notice, setNotice] = useState("");
  const value = draft ?? agent.name;
  const save = async () => {
    if (busy || !value.trim()) return;
    setBusy(true); setError(null); setNotice("");
    try {
      const result = await hub.rename(agent.id, value.trim());
      onUpdated?.(result.agent);
      setDraft(null); setEditing(false); setNotice("名称已保存");
    } catch (failure) { setError(failure); }
    finally { setBusy(false); }
  };
  return <section className="settings-agent-name" aria-label="IP 名称">
    {editing ? <form onSubmit={event => { event.preventDefault(); void save(); }}>
      <div className="settings-agent-name-controls">
        <Field label="IP 名称" id={`agent-label-${agent.id}`}><input autoFocus value={value}
          onChange={event => setDraft(event.target.value)} disabled={busy} /></Field>
        <Button  disabled={busy || !value.trim()} type="submit">{busy ? "正在保存…" : "保存名称"}</Button>
        <Button variant="secondary" disabled={busy} type="button" onClick={() => {
          setDraft(null); setEditing(false); setError(null);
        }}>取消</Button>
      </div>
      <p className="micro">只改列表里的名称，不改对外身份。</p>
    </form> : <Button variant="ghost" type="button" onClick={() => {
      setDraft(agent.name); setEditing(true); setNotice("");
    }}><Icon name="pencil-ruler" size={16} />编辑名称</Button>}
    {notice ? <span className="micro" role="status">{notice}</span> : null}
    {error ? <ErrorState error={error} /> : null}
    {draftError ? <p role="alert">{draftError}</p> : null}
  </section>;
}

/**
 * An Agent's own settings page, shown at `route` whenever the Hub's address asks for one.
 *
 * The page does not know the Hub's name for its Agent; the Hub tells it once it can hear (`pa.settings.ready`),
 * and again when the name or the Agent's state changes. What only the Hub can do to an Agent is drawn at the end of
 * that page's overview with its deletion control. Lifecycle controls stay above this frame in the Hub.
 * Updates from the frame are checked against this Agent before they reach the Hub.
 */
function AgentSettingsFrame({ agent, active, route, onDeleted, onAgentUpdated }: {
  agent: AgentInstance; active: boolean; route?: string; onDeleted?: (id: string) => void;
  onAgentUpdated?: (agent: AgentInstance) => void;
}) {
  const [ready, setReady] = useState(false);
  const [src] = useState(() => `/agents/${encodeURIComponent(agent.id)}/?workspace=settings${route || "#/connections"}`);
  const heard = useCallback((data: AgentFrameMessage) => {
    if (data.type === "pa.settings.ready") setReady(true);
    else if (data.type === "pa.settings.scope" && active && ["agent", "capabilities", "scenes"].includes(String(data.scope))) {
      const next = new URLSearchParams(location.hash.split("?")[1]);
      if (next.get("scope") !== data.scope) { next.set("scope", String(data.scope)); navigateWorkbench("#/settings?" + next); }
    }
    else if (data.type === "pa.settings.agent-deleted" && data.id === agent.id) {
      // One step: leaving first, then the list, in the same turn -- this page and its frame go with the Agent, so a
      // second message would find nobody listening.
      navigateWorkbench("/#/agents");
      onDeleted?.(agent.id);
    } else if (data.type === "pa.settings.agent-updated") {
      const next = data.agent as AgentInstance | undefined;
      if (next && next.id === agent.id) onAgentUpdated?.(next);
    }
  }, [agent.id, active, onDeleted, onAgentUpdated]);
  const frame = useAgentFrame({ visible: active, route, agent, onMessage: heard });
  useEffect(() => {
    if (ready) frame.ref.current?.contentWindow?.postMessage({ type: "pa.settings.agent", id: agent.id, name: agent.name, instance: agent }, location.origin);
  }, [ready, agent, frame.ref]);
  return <div className="settings-frame-shell" hidden={!active}>
    {!ready ? <div className="settings-frame-loading"><LoadingState label="正在打开这个 IP 的配置" /></div> : null}
    <iframe ref={frame.ref} aria-busy={!ready} title={`${agent.name || "创作助手"} 的设置`} src={src} onLoad={frame.onLoad} />
  </div>;
}

/** A scoped switch menu; opening it does not load or run any other Agent. */
export function AgentPicker({ agents, selected, pick }: { agents: AgentInstance[]; selected?: AgentInstance; pick: (id: string) => void }) {
  return <PopMenu label="正在配置的 IP" align="start" className="settings-agent-picker" triggerClass="btn pa-btn ghost settings-agent-trigger"
    trigger={<>
      {selected ? <Avatar name={selected.name} identity={selected.id} className="settings-avatar" /> : null}
      <strong data-quoted="true">{selected ? selected.name || "未命名 Agent" : "选择要配置的 Agent"}</strong>
      <Icon name="down" size={16} />
    </>}>
    {agents.map(agent => {
      const status = instanceStatus(agent);
      const current = selected?.id === agent.id;
      return <MenuItem key={agent.id} current={current} onSelect={() => pick(agent.id)}
        note={current ? <Icon name="done" size={16} /> : undefined}>
        <span className="settings-agent-option">
          <Avatar name={agent.name} identity={agent.id} />
          <span className="settings-agent-option-copy">
            <span data-quoted="true">{agent.name || "未命名 Agent"}</span>
            <span className="settings-agent-option-state"><span className={`settings-dot is-${status.tone}`} aria-hidden="true" />{status.label}</span>
          </span>
        </span>
      </MenuItem>;
    })}
  </PopMenu>;
}

export function PlatformSettings({ accountName, onLogout, preview = false }: { accountName: string; onLogout?: () => void; preview?: boolean }) {
  return <div className="workspace-settings-content settings-groups">
      {!preview ? <AccountModelUsage /> : <section className="settings-group"><h2 className="settings-group-title">平台额度</h2><p>¥100.00（演示） · 所有 Agent 共用</p></section>}
      <section className="settings-group" aria-label="账号">
        <h2 className="settings-group-title">账号</h2>
        <div className="settings-list">
          <section className="settings-block workspace-setting-row" aria-label="当前账号">
            <div><h3>当前账号</h3><p data-quoted="true">{accountName}</p></div>
            {onLogout ? <Button variant="secondary" type="button" onClick={onLogout}>退出登录</Button> : null}
          </section>
          {preview ? <section className="settings-block workspace-setting-row"><div><h3>我的电脑</h3><p>演示电脑 TYG · 在线</p></div></section> : <a className="settings-row" href="/#/clients">
            <span className="settings-row-main"><span className="settings-row-title">我的电脑</span>
              <span className="settings-row-desc">管理连接到账号的客户端</span></span><Icon name="arrow" size={18} />
          </a>}
          {!preview ? <><AccountEmail accountName={accountName} /><AccountPassword accountName={accountName} /></> : <p className="small muted">演示账号 · 不修改真实登录信息</p>}
        </div>
      </section>
      <AppearanceSettings />
    </div>;
}
